import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

type DayRow = {
  dayOfWeek: DayKey;
  dayLabel: string;
  startTime: string;
  endTime: string;
  sameAsMonday: boolean;
};

export default function OperatingHours() {
  const [days, setDays] = useState<DayRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get<DayRow[]>("/operating-hours", {
        params: { fuelCategory: "petrol_diesel_hybrid" },
      });
      setDays(res.data);
    } catch {
      setMessage("Failed to load operating hours");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const monday = days.find((d) => d.dayOfWeek === "mon");

  const updateDay = (
    dayOfWeek: DayKey,
    field: "startTime" | "endTime",
    value: string,
  ) => {
    setDays((prev) =>
      prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, [field]: value } : d)),
    );
  };

  const toggleSameAsMonday = (dayOfWeek: DayKey, checked: boolean) => {
    setDays((prev) =>
      prev.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        if (!checked) return { ...d, sameAsMonday: false };
        const mon = prev.find((x) => x.dayOfWeek === "mon");
        return {
          ...d,
          sameAsMonday: true,
          startTime: mon?.startTime ?? d.startTime,
          endTime: mon?.endTime ?? d.endTime,
        };
      }),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      await axiosInstance.put("/operating-hours", {
        fuelCategory: "petrol_diesel_hybrid",
        days: days.map((d) => ({
          dayOfWeek: d.dayOfWeek,
          startTime: d.startTime,
          endTime: d.endTime,
          sameAsMonday: d.sameAsMonday,
        })),
      });
      setMessage("Operating hours saved");
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <PageBreadcrumb pageTitle="Operating Hours" />

      <div className="mt-4 flex gap-2 border-b">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium border-b-2 -mb-px border-brand-500 text-brand-600 dark:text-brand-400"
        >
          Petrol, Diesel &amp; Hybrid
        </button>
      </div>

      <h2 className="mt-6 text-base font-semibold text-gray-800">
        What are the times of the day/night you will do pickups?
      </h2>
      <p className="mt-2 text-sm text-gray-600">
        Your last available pickup time may commit you to long-distance or late-night jobs — ensure
        drivers are available. Use{" "}
        <Link to="/service-pauses" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
          Pause Availability
        </Link>{" "}
        for full-day closures.
      </p>

      {loading ? (
        <p className="mt-8 text-gray-500">Loading...</p>
      ) : (
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] divide-y">
          {days.map((row) => {
            const isMonday = row.dayOfWeek === "mon";
            const hidden = !isMonday && row.sameAsMonday;

            return (
              <div
                key={row.dayOfWeek}
                className="px-6 py-4 flex flex-wrap items-center gap-4 justify-between"
              >
                <span className="w-28 font-medium text-gray-800">{row.dayLabel}</span>

                {hidden ? (
                  <p className="text-sm text-gray-500 italic flex-1">
                    Same time slots as Monday ({monday?.startTime ?? "—"} –{" "}
                    {monday?.endTime ?? "—"})
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-6 flex-1">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Start time (First pickup)
                      </label>
                      <input
                        type="time"
                        className="border rounded-lg px-3 py-2"
                        value={row.startTime}
                        onChange={(e) =>
                          updateDay(row.dayOfWeek, "startTime", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        End time (Final pickup)
                      </label>
                      <input
                        type="time"
                        className="border rounded-lg px-3 py-2"
                        value={row.endTime}
                        onChange={(e) =>
                          updateDay(row.dayOfWeek, "endTime", e.target.value)
                        }
                      />
                    </div>
                  </div>
                )}

                {!isMonday && (
                  <label className="flex items-center gap-2 text-sm text-gray-600 shrink-0">
                    <input
                      type="checkbox"
                      checked={row.sameAsMonday}
                      onChange={(e) =>
                        toggleSameAsMonday(row.dayOfWeek, e.target.checked)
                      }
                    />
                    Same time slots as Monday
                  </label>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex items-center gap-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save operating hours"}
        </button>
        {message && (
          <p
            className={`text-sm ${
              message.includes("Failed") || message.includes("failed")
                ? "text-red-600"
                : "text-green-600"
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
